import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Search, Eye, Trash2, MessageSquare, MailOpen, Reply, Sparkles,
  Mail, Phone, MessageCircle, Clock, Pencil, StickyNote,
} from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import { Pagination } from '../../common/Select/Select'
import {
  fetchContacts,
  updateContact,
  deleteContact,
  clearContactError,
} from './services/contactSlice'

const STATUS_BADGE = {
  new: 'badge-pending',
  read: 'badge-gold',
  replied: 'badge-active',
}

const STATUS_META = {
  new: { label: 'New', color: '#EF4444', icon: Sparkles },
  read: { label: 'Read', color: '#F59E0B', icon: MailOpen },
  replied: { label: 'Replied', color: '#10B981', icon: Reply },
}

const formatDate = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—')

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?'

const waLink = (phone = '') => {
  const digits = phone.replace(/\D/g, '')
  if (!digits) return null
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`
}

export default function Contact() {
  const dispatch = useDispatch()
  const { list: messages, isLoading, isSaving, isError, message } =
    useSelector((state) => state.contact)

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [viewOpen, setViewOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [edit, setEdit] = useState({ status: 'new', admin_note: '' })

  useEffect(() => { dispatch(fetchContacts()) }, [dispatch])

  const filtered = useMemo(() => messages.filter((m) => {
    const q = search.toLowerCase()
    const matchesSearch =
      m.name.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q) ||
      (m.phone || '').includes(q) ||
      m.message.toLowerCase().includes(q)
    const matchesStatus = filter === 'all' ? true : m.status === filter
    return matchesSearch && matchesStatus
  }), [messages, search, filter])

  useEffect(() => { setPage(1) }, [search, filter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const openView = (m) => {
    dispatch(clearContactError())
    setActive(m)
    setEdit({ status: m.status, admin_note: m.admin_note || '' })
    setViewOpen(true)
    // On first open, if the message is still "new", mark it as "read" automatically.
    if (m.status === 'new') {
      dispatch(updateContact({ id: m.id, status: 'read' })).then((r) => {
        if (r.meta.requestStatus === 'fulfilled') {
          setActive(r.payload)
          setEdit((e) => ({ ...e, status: 'read' }))
        }
      })
    }
  }

  const saveEdit = async () => {
    const result = await dispatch(updateContact({ id: active.id, ...edit }))
    if (result.meta.requestStatus === 'fulfilled') {
      setActive(result.payload)
      setViewOpen(false)
    }
  }

  const onDelete = async () => {
    const result = await dispatch(deleteContact(active.id))
    if (result.meta.requestStatus === 'fulfilled') {
      setDeleteOpen(false)
      setActive(null)
    }
  }

  const count = (s) => messages.filter((m) => m.status === s).length
  const pct = (n) => (messages.length ? Math.round((n / messages.length) * 100) : 0)

  const stats = [
    { label: 'Total Messages', value: messages.length, icon: MessageSquare, color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', variant: 'indigo' },
    { label: 'New', value: count('new'), icon: Sparkles, color: '#EF4444', colorGlow: 'rgba(239,68,68,0.15)', variant: 'red' },
    { label: 'Read', value: count('read'), icon: MailOpen, color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', variant: 'amber' },
    { label: 'Replied', value: count('replied'), icon: Reply, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', variant: 'green' },
  ]

  const wa = active ? waLink(active.phone || '') : null

  return (
    <div className="dash-page page-in">
      {/* ── PAGE HEADER ── */}
      <div className="pg-header">
        <h1 className="page-title" style={{ margin: 0 }}>Contact Us</h1>
      </div>

      {/* ── STATS ── */}
      <div className="stats-grid cols-4">
        {stats.map((s) => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{
              position: 'absolute', top: 0, right: 0, width: 110, height: 110,
              borderRadius: '50%', background: s.colorGlow, filter: 'blur(30px)',
              transform: 'translate(30%, -30%)', pointerEvents: 'none',
            }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{ color: s.color, background: `${s.color}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.label === 'Total Messages' ? '100%' : `${pct(s.value)}%`}
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg,${s.color}88,transparent)`,
              borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
            }} />
          </div>
        ))}
      </div>

      {/* ── TABLE ── */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <label className="tb-search">
            <Search size={15} aria-hidden="true" />
            <input
              type="text"
              placeholder="Search name, email, phone…"
              aria-label="Search messages"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="view-tabs" style={{ margin: 0 }}>
            {['all', 'new', 'read', 'replied'].map((f) => (
              <button key={f} className={`vtab${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th><th>Name</th><th>Contact</th><th>Message</th><th>Date</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 'var(--space-lg)', color: 'var(--text3)' }}>No messages found</td></tr>
              )}
              {paginated.map((m) => (
                <tr key={m.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>#{m.id}</td>
                  <td style={{ fontWeight: m.status === 'new' ? 700 : 600, color: 'var(--text1)' }}>{m.name}</td>
                  <td>
                    <div style={{ fontSize: 'var(--fs-sm)' }}>{m.email}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{m.phone || ''}</div>
                  </td>
                  <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text2)' }}>{m.message}</td>
                  <td style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{formatDate(m.created_at)}</td>
                  <td><span className={`badge ${STATUS_BADGE[m.status]}`}>{m.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openView(m)}><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(m); setDeleteOpen(true) }}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!isLoading && filtered.length > 0 && (
          <Pagination
            total={filtered.length}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* ── VIEW / UPDATE MODAL ── */}
      <Modal
        open={viewOpen}
        title="Message Details"
        subtitle={active ? `Received ${formatDate(active.created_at)}` : ''}
        onClose={() => setViewOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setViewOpen(false)}>Close</button>
            <button className="btn btn-primary" onClick={saveEdit} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        )}
      >
        {isError && message && <div className="login-error" style={{ marginBottom: 12 }}>{message}</div>}
        {active && (
          <div className="cm">
            {/* sender card */}
            <div className="cm-sender">
              <div className="cm-avatar">{initials(active.name)}</div>
              <div className="cm-sender-info">
                <div className="cm-name">{active.name}</div>
                <a className="cm-line" href={`mailto:${active.email}`}>
                  <Mail size={13} /> {active.email}
                </a>
                {active.phone && (
                  <a className="cm-line" href={`tel:${active.phone}`}>
                    <Phone size={13} /> {active.phone}
                  </a>
                )}
              </div>
              <span className={`badge ${STATUS_BADGE[active.status]}`}>{active.status}</span>
            </div>

            {/* quick actions */}
            <div className="cm-actions">
              <a className="cm-action" href={`mailto:${active.email}?subject=Re: Your message to Supadaa`}>
                <Mail size={14} /> Reply by Email
              </a>
              {active.phone && (
                <a className="cm-action" href={`tel:${active.phone}`}>
                  <Phone size={14} /> Call
                </a>
              )}
              {wa && (
                <a className="cm-action cm-action--wa" href={wa} target="_blank" rel="noreferrer">
                  <MessageCircle size={14} /> WhatsApp
                </a>
              )}
            </div>

            {/* message */}
            <div className="cm-section">
              <div className="cm-label"><MessageSquare size={13} /> Message</div>
              <div className="cm-message">{active.message}</div>
            </div>

            {/* meta */}
            <div className="cm-meta">
              <span><Clock size={12} /> {formatDate(active.created_at)}</span>
            </div>

            {/* status pills */}
            <div className="cm-section">
              <div className="cm-label">Status</div>
              <div className="cm-pills">
                {Object.entries(STATUS_META).map(([key, meta]) => {
                  const Icon = meta.icon
                  const on = edit.status === key
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`cm-pill${on ? ' on' : ''}`}
                      style={on ? { color: meta.color, borderColor: meta.color, background: `${meta.color}18` } : undefined}
                      onClick={() => setEdit({ ...edit, status: key })}
                    >
                      <Icon size={13} /> {meta.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* note */}
            <div className="cm-section">
              <div className="cm-label"><StickyNote size={13} /> Admin Note <small>(only visible to admins)</small></div>
              <textarea
                className="input"
                rows={3}
                placeholder="Add an internal note…"
                value={edit.admin_note}
                onChange={(e) => setEdit({ ...edit, admin_note: e.target.value })}
              />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete message"
        message={`Delete message from ${active?.name || 'this person'}? This cannot be undone.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }
        textarea.input { resize: vertical; min-height: 56px; }

        /* ---- page header (title left, action right) ---- */
        .pg-header {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--space-md); flex-wrap: wrap;
        }

        /* ---- toolbar search (stretches between the title and the tabs) ---- */
        .tb-search {
          flex: 1 1 260px; min-width: 0; margin: 0;
          display: flex; align-items: center; gap: 10px;
          height: 38px; padding: 0 12px; cursor: text;
          border: 1px solid var(--border); border-radius: 10px;
          background: #f6f8ff; color: var(--text3);
          transition: border-color .18s ease, box-shadow .18s ease;
        }
        .tb-search:focus-within {
          border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.16);
        }
        .tb-search svg { flex-shrink: 0; }
        .tb-search input {
          flex: 1; min-width: 0; height: 100%; padding: 0; margin: 0;
          border: 0; outline: 0; box-shadow: none; background: transparent;
          font: inherit; font-size: var(--fs-sm); color: var(--text1);
        }
        .tb-search input::placeholder { color: var(--text3); }

        .login-error {
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5; font-size: 0.875rem;
          padding: 0.6rem 0.9rem; border-radius: 10px;
        }

        /* ---- message details modal ---- */
        .cm { display: flex; flex-direction: column; gap: 16px; }
        .cm-sender {
          display: flex; align-items: center; gap: 14px; padding: 14px;
          border: 1px solid var(--border); border-radius: var(--radius-lg, 12px);
          background: var(--bg-raised);
        }
        .cm-avatar {
          width: 52px; height: 52px; flex-shrink: 0; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 1.05rem; color: #fff;
          background: linear-gradient(135deg, #4F46E5, #9333EA);
          box-shadow: 0 4px 14px rgba(79, 70, 229, 0.35);
        }
        .cm-sender-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
        .cm-name { font-weight: 700; font-size: 1rem; color: var(--text1); }
        .cm-line {
          display: inline-flex; align-items: center; gap: 6px; width: fit-content; max-width: 100%;
          font-size: var(--fs-sm, 13px); color: var(--text2); text-decoration: none;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .cm-line:hover { color: var(--accent-hover); }

        .cm-actions { display: flex; flex-wrap: wrap; gap: 8px; }
        .cm-action {
          display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px;
          font-size: var(--fs-xs, 12px); font-weight: 600; text-decoration: none;
          color: var(--text1); border: 1px solid var(--border); border-radius: 999px;
          background: var(--bg-raised); transition: all .15s;
        }
        .cm-action:hover { border-color: var(--accent-hover); color: var(--accent-hover); }
        .cm-action--wa { color: #10B981; border-color: rgba(16, 185, 129, 0.4); }
        .cm-action--wa:hover { background: rgba(16, 185, 129, 0.12); color: #10B981; border-color: #10B981; }

        .cm-section { display: flex; flex-direction: column; gap: 8px; }
        .cm-label {
          display: flex; align-items: center; gap: 6px;
          font-size: var(--fs-xs, 12px); font-weight: 600; letter-spacing: .04em;
          text-transform: uppercase; color: var(--text3);
        }
        .cm-label small { text-transform: none; letter-spacing: 0; font-weight: 400; }
        .cm-message {
          white-space: pre-wrap; word-break: break-word; line-height: 1.6;
          padding: 14px 16px; color: var(--text1);
          border: 1px solid var(--border); border-left: 3px solid #4F46E5;
          border-radius: var(--radius-md, 10px); background: var(--bg-raised);
          max-height: 220px; overflow-y: auto;
        }
        .cm-meta {
          display: flex; flex-wrap: wrap; gap: 16px;
          font-size: var(--fs-xs, 12px); color: var(--text3);
        }
        .cm-meta span { display: inline-flex; align-items: center; gap: 5px; }

        .cm-pills { display: flex; flex-wrap: wrap; gap: 8px; }
        .cm-pill {
          display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px;
          font-size: var(--fs-sm, 13px); font-weight: 600; cursor: pointer;
          color: var(--text3); background: transparent;
          border: 1px solid var(--border); border-radius: 999px; transition: all .15s;
        }
        .cm-pill:hover { color: var(--text1); border-color: var(--text3); }
        .cm-pill.on { cursor: default; }

        @media (max-width: 480px) {
          .cm-sender { flex-wrap: wrap; }
        }
      `}</style>
    </div>
  )
}